import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loan360Path } from '../../utils/loanNavigation';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import type { ColDef } from 'ag-grid-community';
import { InstitutionalChart, InstitutionalDataGrid } from '../shared';
import { parseLocalDate } from './LoanFacilityCardDrawer';

const formatDateOnly = (value?: string | null) => {
  const parsed = parseLocalDate(value);
  return parsed ? parsed.toLocaleDateString() : '-';
};

const Container = styled.div<{ $embedded?: boolean }>`
  max-width: 1800px;
  margin: 0 auto;
  padding: ${props => props.$embedded ? '0' : '2rem'};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h2`
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const ActionButton = styled.button<{ $variant?: string }>`
  padding: 0.6rem 1.25rem;
  border-radius: 8px;
  border: ${props => props.$variant === 'primary' ? 'none' : '1px solid rgba(255,255,255,0.1)'};
  font-weight: 600;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$variant === 'primary'
    ? 'linear-gradient(135deg, #f59e0b, #d97706)'
    : 'rgba(255, 255, 255, 0.05)'};
  color: white;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  }

  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
`;

const AlertsSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: white;
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AlertsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1rem;
`;

const AlertCard = styled(motion.div)<{ $severity: string }>`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(51, 65, 85, 0.9) 100%);
  border: 1px solid ${props => props.$severity === 'BREACHED' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(245, 158, 11, 0.3)'};
  border-radius: 12px;
  padding: 1.25rem;
  border-left: 4px solid ${props => props.$severity === 'BREACHED' ? '#ef4444' : '#f59e0b'};
`;

const AlertHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
`;

const AlertTitle = styled.div`
  color: white;
  font-weight: 600;
  font-size: 0.95rem;
`;

const AlertBadge = styled.span<{ $severity: string }>`
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  ${props => props.$severity === 'BREACHED' ? `
    background: rgba(239, 68, 68, 0.1); color: #ef4444; border: 1px solid rgba(239, 68, 68, 0.2);
  ` : `
    background: rgba(245, 158, 11, 0.1); color: #f59e0b; border: 1px solid rgba(245, 158, 11, 0.2);
  `}
`;

const AlertDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const AlertDetail = styled.div``;

const DetailLabel = styled.div`
  color: #64748b;
  font-size: 0.7rem;
  text-transform: uppercase;
  margin-bottom: 2px;
`;

const DetailValue = styled.div<{ $highlight?: boolean }>`
  color: ${props => props.$highlight ? '#ef4444' : 'white'};
  font-size: 0.875rem;
  font-weight: ${props => props.$highlight ? '700' : '500'};
`;

const AlertActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const SmallButton = styled.button`
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #94a3b8;
  &:hover:not(:disabled) { background: rgba(59, 130, 246, 0.1); border-color: #3b82f6; color: white; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div<{ $color?: string }>`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(51, 65, 85, 0.9) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.25rem;
  border-left: 4px solid ${props => props.$color || '#3b82f6'};
`;

const StatLabel = styled.div`
  color: #94a3b8;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
`;

const StatValue = styled.div`
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
`;

const FilterBar = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;
`;

const FilterChip = styled.button<{ $active: boolean }>`
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid ${props => props.$active ? '#f59e0b' : 'rgba(255,255,255,0.1)'};
  background: ${props => props.$active ? 'rgba(245,158,11,0.15)' : 'transparent'};
  color: ${props => props.$active ? '#fbbf24' : '#94a3b8'};
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { border-color: #f59e0b; color: #fbbf24; }
`;

// Scope filter pill — color-tinted by scope so the active source is obvious.
const ScopeChip = styled.button<{ $active: boolean; $color: string }>`
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid ${props => props.$active ? props.$color : 'rgba(255,255,255,0.1)'};
  background: ${props => props.$active ? `${props.$color}22` : 'transparent'};
  color: ${props => props.$active ? props.$color : '#94a3b8'};
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  &:hover { border-color: ${props => props.$color}; color: ${props => props.$color}; }
`;

const ScopeChipDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$color};
`;

const ScopeBadge = styled.span<{ $color: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${props => props.$color};
  background: ${props => `${props.$color}1a`};
  border: 1px solid ${props => `${props.$color}33`};
`;

const SearchInput = styled.input`
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid rgba(255,255,255,0.1);
  background: rgba(255,255,255,0.05);
  color: white;
  font-size: 0.8rem;
  outline: none;
  width: 200px;
  &:focus { border-color: #f59e0b; }
  &::placeholder { color: #64748b; }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 1.5rem;
  align-items: start;
`;

const StatusBadge = styled.span<{ $status: string }>`
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  ${props => {
    switch (props.$status) {
      case 'PASSED': return 'background: rgba(16,185,129,0.1); color: #10b981; border: 1px solid rgba(16,185,129,0.2);';
      case 'BREACHED': return 'background: rgba(239,68,68,0.1); color: #ef4444; border: 1px solid rgba(239,68,68,0.2);';
      case 'WARNING': return 'background: rgba(245,158,11,0.1); color: #f59e0b; border: 1px solid rgba(245,158,11,0.2);';
      case 'CURED': return 'background: rgba(59,130,246,0.1); color: #3b82f6; border: 1px solid rgba(59,130,246,0.2);';
      case 'WAIVED': return 'background: rgba(139,92,246,0.1); color: #8b5cf6; border: 1px solid rgba(139,92,246,0.2);';
      default: return 'background: rgba(148,163,184,0.1); color: #94a3b8; border: 1px solid rgba(148,163,184,0.2);';
    }
  }}
`;

const CushionIndicator = styled.div<{ $level: 'safe' | 'warning' | 'danger' }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${props => props.$level === 'safe' ? '#10b981' : props.$level === 'warning' ? '#f59e0b' : '#ef4444'};
  }
`;

// Detail panel styles
const DetailPanel = styled(motion.div)`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(51, 65, 85, 0.95) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  position: sticky;
  top: 2rem;
`;

const DetailPanelTitle = styled.h3`
  color: white;
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 0.25rem 0;
`;

const DetailPanelSubtitle = styled.div`
  color: #94a3b8;
  font-size: 0.8rem;
  margin-bottom: 1.25rem;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

const DetailItem = styled.div``;

const DetailItemLabel = styled.div`
  color: #64748b;
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 2px;
`;

const DetailItemValue = styled.div<{ $color?: string }>`
  color: ${props => props.$color || 'white'};
  font-size: 0.9rem;
  font-weight: 600;
`;

// Trend visualization
const TrendSection = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255,255,255,0.1);
`;

const TrendTitle = styled.div`
  color: #94a3b8;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 0.75rem;
`;

const HealthScore = styled.div<{ $score: number }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.75rem;
  background: ${props => props.$score >= 80 ? 'rgba(16,185,129,0.1)' : props.$score >= 50 ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)'};
  border: 1px solid ${props => props.$score >= 80 ? 'rgba(16,185,129,0.2)' : props.$score >= 50 ? 'rgba(245,158,11,0.2)' : 'rgba(239,68,68,0.2)'};
  border-radius: 10px;
  margin-bottom: 1rem;
`;

const HealthScoreValue = styled.div<{ $score: number }>`
  font-size: 1.5rem;
  font-weight: 800;
  color: ${props => props.$score >= 80 ? '#10b981' : props.$score >= 50 ? '#f59e0b' : '#ef4444'};
`;

const HealthScoreLabel = styled.div`
  color: #94a3b8;
  font-size: 0.7rem;
  line-height: 1.3;
`;

const DetailActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255,255,255,0.1);
`;

const DetailActionButton = styled.button<{ $color?: string }>`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: 1px solid ${props => props.$color ? `${props.$color}40` : 'rgba(255,255,255,0.1)'};
  background: ${props => props.$color ? `${props.$color}15` : 'rgba(255,255,255,0.05)'};
  color: ${props => props.$color || '#94a3b8'};
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;
  &:hover:not(:disabled) { background: ${props => props.$color ? `${props.$color}25` : 'rgba(255,255,255,0.1)'}; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

// Auditor-validation chip — surfaces the deterministic second-pair-of-eyes check
// (CovenantAuditorService). Emerald when the engine independently re-derived and
// matched the operator/LLM-extracted value; neutral slate when not yet validated.
const AuditorChip = styled.div<{ $validated: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.7rem 0.8rem;
  margin: 1rem 0 0.25rem 0;
  border-radius: 10px;
  border: 1px solid ${props => props.$validated ? 'rgba(16,185,129,0.28)' : 'rgba(148,163,184,0.22)'};
  background: ${props => props.$validated ? 'rgba(16,185,129,0.08)' : 'rgba(148,163,184,0.06)'};
`;

const AuditorChipIcon = styled.div<{ $validated: boolean }>`
  font-size: 0.95rem;
  line-height: 1.1;
  color: ${props => props.$validated ? '#10b981' : '#64748b'};
  flex-shrink: 0;
  margin-top: 1px;
`;

const AuditorChipBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const AuditorChipTitle = styled.div<{ $validated: boolean }>`
  font-size: 0.78rem;
  font-weight: 700;
  color: ${props => props.$validated ? '#34d399' : '#94a3b8'};
`;

const AuditorChipDetail = styled.div`
  font-size: 0.72rem;
  line-height: 1.4;
  color: #94a3b8;
`;

// Interfaces
interface CovenantAlert {
  id: number;
  covenantName: string;
  borrowerName: string;
  covenantType: string;
  metricType?: string;
  testResult: string;
  actualValue: number;
  thresholdValue: number;
  cushion: number;
  testDate: string;
  nextTestDate: string;
}

interface Covenant {
  id: number;
  covenantName: string;
  borrowerName: string;
  covenantType: string;
  metricType?: string;
  category?: string;
  currentStatus: string;
  thresholdValue: number;
  actualValue: number;
  cushion: number;
  warningThreshold: number;
  frequency: string;
  nextTestDate: string;
  trendHistory?: number[];
  // Real trend data from /api/credit/covenants/{id}/trend.
  // testDate is ISO date; cushion is %; value is the actual covenant value.
  trendPoints?: { testDate: string; value: number; cushion: number; status: string }[];
  // Number of quarters until projected breach at current rate of change.
  // Null = no deterioration / flat / improving. Single-digit triggers amber.
  quartersToBreachEst?: number | null;
  // IMPROVING | STABLE | DETERIORATING — from CovenantTrendService.
  trendDirection?: string;
  // Scope discriminator from backend: LEVERAGED_LOAN | PRIVATE_CREDIT | ABF | RE_DEBT | PORTCO_DEBT
  // Drives the unified Covenant Center scope filter and per-row source chip.
  scope?: string;
  // Anchoring silo PK + source from the backend /covenants row. Used to deep-link
  // this covenant to its loan in the Credit Operating System Loan 360 drawer.
  // loanSource mirrors scope; only LEVERAGED_LOAN / PRIVATE_CREDIT have a Loan 360
  // surface today, so the drill affordance is hidden for the other scopes.
  loanId?: number | null;
  loanSource?: string;
  // Independent-validation state from the latest covenant_test_result. The auditor
  // agent (CovenantAuditorService) deterministically re-derives actual_value from
  // the parsed source document and stamps these fields. Surfacing them makes the
  // "LLM extracts, deterministic engine verifies" control visible in the UI.
  auditorValidated?: boolean;
  auditorActualValue?: number | null;
  auditorNotes?: string | null;
  auditorRunAt?: string | null;
}

/**
 * #20/D6: covenant-evidence exception row from
 * GET /api/credit/covenants/evidence-exceptions. One row per evidence entry
 * the auditor could not validate. severity CRITICAL = the auditor's
 * recomputed value would flip the certified PASS/FAIL verdict.
 */
interface EvidenceException {
  exceptionId: number;
  severity: string;
  queueStatus: string;
  title: string;
  description: string;
  resolution?: string | null;
  resolvedBy?: string | null;
  resolvedAt?: string | null;
  createdAt?: string;
  loanSource?: string;
  loanId?: number | null;
  pcLoanId?: number | null;
  evidenceId: number;
  covenantId: number;
  testResultId?: number | null;
  periodEndDate?: string | null;
  sourceDocumentName?: string | null;
  validationStatus?: string;
  covenantName?: string;
  thresholdType?: string | null;
  certifiedValue?: number | null;
  auditorValue?: number | null;
  thresholdValue?: number | null;
  certifiedStatus?: string | null;
  auditorNotes?: string | null;
  borrowerName?: string | null;
}

/**
 * Scope discriminator → display config. The scopes correspond to which
 * anchoring FK is set on covenant_definition (loan_id / private_credit_loan_id
 * / abf_facility_id / real_estate_loan_id / portco_debt_id). Colors are intentionally distinct
 * so a row's source is identifiable at-a-glance in the unified table.
 */
const SCOPE_LABELS: Record<string, { label: string; color: string }> = {
  LEVERAGED_LOAN: { label: 'Leveraged Loan', color: '#f59e0b' },
  PRIVATE_CREDIT: { label: 'Private Credit', color: '#3b82f6' },
  ABF: { label: 'ABF', color: '#38bdf8' },
  RE_DEBT: { label: 'RE Debt', color: '#10b981' },
  PORTCO_DEBT: { label: 'Portco Debt', color: '#8b5cf6' },
  UNKNOWN: { label: 'Unknown', color: '#64748b' },
};

const SCOPE_ORDER: Array<{ key: string; short: string }> = [
  { key: 'all', short: 'All' },
  { key: 'LEVERAGED_LOAN', short: 'Leveraged Loans' },
  { key: 'PRIVATE_CREDIT', short: 'Private Credit' },
  { key: 'ABF', short: 'ABF' },
  { key: 'RE_DEBT', short: 'RE Debt' },
  { key: 'PORTCO_DEBT', short: 'Portco Debt' },
];

/**
 * Format a covenant metric value with the right unit suffix.
 *
 * - LTV / Debt Yield / ABF pool ratios → percent (75.00%)
 * - Coverage / leverage / DSCR → multiple (1.30x)
 * - Liquidity / cash flow (>10K) → millions ($50M)
 *
 * Threshold values are stored at face value on the covenant_definition row
 * (LTV/yield in percent, ratios as decimals) — see RealEstateLoan unit
 * convention memo.
 */
const PERCENT_METRICS = new Set([
  'LOAN_TO_VALUE',
  'DEBT_YIELD',
  'ADVANCE_RATE',
  'OVERCOLLATERALIZATION',
  'DELINQUENCY',
  'NET_LOSS',
  'EXCESS_SPREAD',
]);
const formatMetricValue = (value: number | undefined | null, metricType?: string): string => {
  const v = typeof value === 'number' ? value : 0;
  if (metricType && PERCENT_METRICS.has(metricType)) {
    return `${v.toFixed(2)}%`;
  }
  if (Math.abs(v) > 10000) {
    return `$${(v / 1e6).toFixed(1)}M`;
  }
  return `${v.toFixed(2)}x`;
};

interface CovenantMonitoringScreenProps {
  embedded?: boolean;
}

const CovenantMonitoringScreen: React.FC<CovenantMonitoringScreenProps> = ({ embedded = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  // scope=PORTCO_DEBT|RE_DEBT|ABF|PRIVATE_CREDIT|LEVERAGED_LOAN — drives the unified
  // Covenant Center filter and is honored on deep-link from Alts surfaces and
  // the retired portco heatmap redirect.
  const scopeFromUrl = searchParams.get('scope');
  const initialScope = scopeFromUrl && SCOPE_LABELS[scopeFromUrl] ? scopeFromUrl : 'all';
  const [scopeFilter, setScopeFilter] = useState<string>(initialScope);

  const [alerts, setAlerts] = useState<CovenantAlert[]>([]);
  const [covenants, setCovenants] = useState<Covenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [selectedCovenant, setSelectedCovenant] = useState<Covenant | null>(null);
  const [evaluating, setEvaluating] = useState(false);
  // #20/D6 — evidence exceptions queue + list-level validation badges/filter.
  const [evidenceExceptions, setEvidenceExceptions] = useState<EvidenceException[]>([]);
  const [validationFilter, setValidationFilter] = useState<string>('all');
  const [view, setView] = useState<'covenants' | 'exceptions'>('covenants');
  const [resolvingId, setResolvingId] = useState<number | null>(null);
  // Re-fetch whenever scope changes — push the filter to the backend so the
  // count/badges in StatsRow accurately reflect the chosen slice rather than
  // showing the unfiltered universe.
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeFilter]);

  // Sync scope back to URL (without clobbering tab/sub) so the active filter
  // is reflected in the address bar and survives navigation/back-button.
  const handleScopeChange = (next: string) => {
    setScopeFilter(next);
    const params = new URLSearchParams(searchParams);
    if (next === 'all') {
      params.delete('scope');
    } else {
      params.set('scope', next);
    }
    setSearchParams(params, { replace: true });
  };

  // Only LEVERAGED_LOAN / PRIVATE_CREDIT have a Loan 360 surface in the Credit
  // Operating System today (RE debt / ABF / portco debt are not loaded into that
  // drawer). Gate the drill affordance on those two scopes so we never offer a
  // dead-end link.
  const canOpenInLoan360 = useCallback((cov: Covenant): boolean => {
    const LOAN360_SCOPES = new Set(['LEVERAGED_LOAN', 'PRIVATE_CREDIT']);
    return cov.loanId != null && LOAN360_SCOPES.has(String(cov.loanSource || cov.scope || ''));
  }, []);

  // Open a covenant's loan on its Loan 360 page, on the Covenants tab (the reverse of Loan 360's
  // "Open Covenant Center").
  const navigateToLoan = useNavigate();
  const openInLoan360 = useCallback((cov: Covenant) => {
    if (!canOpenInLoan360(cov)) return;
    navigateToLoan(loan360Path(String(cov.loanSource || cov.scope), String(cov.loanId), 'covenants'));
  }, [canOpenInLoan360, navigateToLoan]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const scopeParam = scopeFilter !== 'all' ? `?scope=${scopeFilter}` : '';
      const [alertsRes, covenantsRes, testResultsRes, exceptionsRes] = await Promise.all([
        axios.get('/api/credit/covenants/alerts'),
        axios.get(`/api/credit/covenants${scopeParam}`),
        axios.get('/api/credit/covenants/test-results').catch(() => null),
        axios.get('/api/credit/covenants/evidence-exceptions').catch(() => null),
      ]);
      setEvidenceExceptions(exceptionsRes?.data || []);

      // Build a map of latest test results by covenant ID
      const testResultMap: Record<number, any> = {};
      const isNewerTestResult = (candidate: any, current: any): boolean => {
        if (!current) return true;
        const candidateDate = String(candidate?.testDate || '');
        const currentDate = String(current?.testDate || '');
        if (candidateDate !== currentDate) {
          return candidateDate > currentDate;
        }
        return Number(candidate?.id || 0) > Number(current?.id || 0);
      };
      if (testResultsRes?.data) {
        for (const tr of testResultsRes.data) {
          const covId = tr.covenant?.id || tr.covenantId;
          if (covId && isNewerTestResult(tr, testResultMap[covId])) {
            testResultMap[covId] = tr;
          }
        }
      }

      // Transform alerts from API shape: { covenant: {...}, alertType, message }
      // to flat shape the UI expects
      const rawAlerts = alertsRes.data || [];
      const mappedAlerts: CovenantAlert[] = rawAlerts.map((a: any) => {
        const cov = a.covenant || a;
        const tr = testResultMap[cov.id];
        return {
          id: cov.id,
          covenantName: cov.covenantName || a.covenantName || '-',
          borrowerName: cov.borrowerName || cov.loan?.borrowerName || cov.privateCreditLoan?.borrowerName || cov.realEstateLoan?.loanName || a.borrowerName || '-',
          covenantType: cov.covenantType || a.covenantType || '-',
          metricType: cov.metricType || a.metricType,
          testResult: tr?.status || a.testResult || a.alertType || 'WARNING',
          actualValue: tr?.actualValue ?? a.actualValue ?? 0,
          thresholdValue: cov.thresholdValue ?? a.thresholdValue ?? 0,
          cushion: tr?.cushionPct != null ? Number(tr.cushionPct) * 100 : (a.cushion ?? 0),
          testDate: tr?.testDate || a.testDate || '',
          nextTestDate: a.nextTestDate || '',
        };
      });
      // Active Alerts cards are for real signal — BREACHED / WARNING / FAILED.
      // TEST_DUE is a scheduling reminder (test window open, no result yet) and
      // renders with actual=0.00 / cushion=0.0% because there is no test_result
      // row to read from. Surfacing 24 of those as alert cards above the grid
      // drowns the actual breaches and warnings in noise — TEST_DUE belongs in
      // the grid's Status column / a Next Test column, not in the alerts band.
      // Also drop orphan rows with no resolvable borrower ('-').
      const ACTIONABLE_ALERT_STATUSES = new Set(['BREACHED', 'BREACH', 'WARNING', 'FAILED', 'FAIL']);
      const actionableAlerts = mappedAlerts.filter(a =>
          ACTIONABLE_ALERT_STATUSES.has(String(a.testResult).toUpperCase())
          && a.borrowerName && a.borrowerName !== '-'
      );
      setAlerts(actionableAlerts);

      // Transform covenants from API shape: CovenantDefinition entity
      // to flat shape the UI expects
      const rawCovenants = covenantsRes.data || [];
      // Status normalization: covenant_test_result.status is stored as 'PASS' /
      // 'FAIL' (the engine emits these), while covenant_definition.currentStatus
      // uses the longer 'PASSED' / 'BREACHED' enum. Without normalization the
      // grid shows two visually different pills ('PASS' grey default, 'PASSED'
      // green) for the same underlying state — a CIO reads that as "two status
      // models". Map to the long-form enum that StatusBadge styles cover.
      const normalizeStatus = (raw: any): string => {
        const s = String(raw || '').toUpperCase();
        if (s === 'PASS') return 'PASSED';
        if (s === 'FAIL') return 'BREACHED';
        if (s === 'WARN') return 'WARNING';
        // F-COV-1: a covenant with no test result must NOT read as green "PASSED"
        // with actual 0.00 — that misrepresents an untested covenant as compliant.
        // Empty / scheduling-only states surface as NOT_TESTED (neutral).
        if (!s || s === 'TEST_DUE' || s === 'UNTESTED' || s === 'NOT_TESTED' || s === 'PENDING') return 'NOT_TESTED';
        return s;
      };
      const mappedCovenants: Covenant[] = rawCovenants.map((c: any) => {
        const tr = testResultMap[c.id];
        // No test_result row AND no engine-set currentStatus ⇒ NOT_TESTED (never default PASSED).
        const status = normalizeStatus(tr?.status || c.currentStatus);
        const actualVal = tr?.actualValue ?? c.actualValue ?? 0;
        const thresholdVal = c.thresholdValue ?? 0;
        let cushion = 0;
        if (tr?.cushionPct != null) {
          cushion = Number(tr.cushionPct) * 100;
        } else if (thresholdVal > 0 && actualVal > 0) {
          // For MAX thresholds (leverage): cushion = (threshold - actual) / threshold * 100
          // For MIN thresholds (coverage): cushion = (actual - threshold) / threshold * 100
          const thType = c.thresholdType || 'MAX';
          cushion = thType === 'MIN'
            ? ((actualVal - thresholdVal) / thresholdVal) * 100
            : ((thresholdVal - actualVal) / thresholdVal) * 100;
        }

        const mapped: Covenant = {
          id: c.id,
          covenantName: c.covenantName || '-',
          borrowerName: c.borrowerName || c.loan?.borrowerName || c.privateCreditLoan?.borrowerName || c.realEstateLoan?.loanName || '-',
          covenantType: c.covenantType || '-',
          metricType: c.metricType,
          category: c.covenantCategory || c.category || c.covenantType || '',
          currentStatus: status,
          thresholdValue: thresholdVal,
          actualValue: actualVal,
          cushion: cushion,
          warningThreshold: c.warningThresholdPct ?? c.warningThreshold ?? 15,
          frequency: c.measurementFrequency || c.frequency || 'QUARTERLY',
          nextTestDate: c.nextTestDate || '',
          // trendPoints/quartersToBreachEst/trendDirection hydrated by the
          // trend-fetch pass below. We deliberately do NOT generate a mock
          // sparkline here — empty sparkline is correct when no test history
          // exists.
          trendPoints: undefined,
          quartersToBreachEst: null,
          trendDirection: undefined,
          trendHistory: undefined,
          scope: c.scope || 'UNKNOWN',
          loanId: c.loanId ?? null,
          loanSource: c.loanSource || c.scope || 'UNKNOWN',
          // Auditor validation comes from the latest test_result row (tr), which the
          // CovenantAuditorService stamps. The /covenants definition row itself has
          // no auditor state — it lives on the test result.
          auditorValidated: tr?.auditorValidated ?? false,
          auditorActualValue: tr?.auditorActualValue ?? null,
          auditorNotes: tr?.auditorNotes ?? null,
          auditorRunAt: tr?.auditorRunAt ?? null,
        };
        return mapped;
      });

      // Second pass: hydrate real trend history per covenant. Failures (e.g.
      // a covenant with no test results) leave trendPoints undefined and the
      // UI renders no sparkline rather than fake data.
      const trendResponses = await Promise.all(
        mappedCovenants.map(c =>
          axios.get(`/api/credit/covenants/${c.id}/trend?lookbackQuarters=4`)
            .then(res => ({ id: c.id, data: res.data }))
            .catch(() => ({ id: c.id, data: null }))
        )
      );
      const trendMap: Record<number, any> = {};
      for (const t of trendResponses) {
        if (t.data) trendMap[t.id] = t.data;
      }

      const hydrated: Covenant[] = mappedCovenants.map(c => {
        const t = trendMap[c.id];
        if (!t || !Array.isArray(t.history) || t.history.length === 0) {
          return c;
        }
        // Backend CovenantTrendService returns:
        //   history: [{ testDate, value, threshold, cushion, status }]
        //   estimatedQuartersToBreach, direction
        // Convert cushion to a percentage of threshold for the sparkline,
        // matching the existing trendHistory semantic.
        const points = t.history.map((p: any) => ({
          testDate: p.testDate,
          value: Number(p.value),
          // p.cushion from backend is absolute cushion (actual - threshold).
          // Convert to % of threshold for consistent visualisation with the
          // table's Cushion column. Avoid divide-by-zero.
          cushion: p.threshold && Number(p.threshold) !== 0
            ? (Number(p.cushion) / Number(p.threshold)) * 100
            : Number(p.cushion),
          status: p.status,
        }));
        return {
          ...c,
          trendPoints: points,
          trendHistory: points.map((p: any) => Number(p.cushion.toFixed(2))),
          quartersToBreachEst: t.estimatedQuartersToBreach ?? null,
          trendDirection: t.direction,
        };
      });
      setCovenants(hydrated);
    } catch (error) {
      console.error('Failed to fetch covenant data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEvaluateAll = async () => {
    setEvaluating(true);
    try {
      await axios.post('/api/credit/covenants/evaluate');
      await fetchData();
    } catch {
      // Keep existing data
    } finally {
      setEvaluating(false);
    }
  };

  const getCushionLevel = (cushion: number): 'safe' | 'warning' | 'danger' => {
    if (cushion < 0) return 'danger';
    if (cushion < 15) return 'warning';
    return 'safe';
  };

  const getHealthScore = (covenant: Covenant): number => {
    if (covenant.currentStatus === 'BREACHED') return Math.max(0, 20 + covenant.cushion);
    if (covenant.currentStatus === 'WARNING') return 40 + covenant.cushion;
    if (covenant.currentStatus === 'WAIVED') return 60;
    return Math.min(100, 60 + covenant.cushion);
  };

  const getTrendColor = (value: number): string => {
    if (value < 0) return '#ef4444';
    if (value < 15) return '#f59e0b';
    return '#10b981';
  };

  // #20/D6 — open evidence exceptions per covenant, for list badges and the
  // validation filter. CRITICAL = auditor value would flip the certified verdict.
  const exceptionsByCovenant = useMemo(() => {
    const map: Record<number, { open: number; flip: number }> = {};
    for (const ex of evidenceExceptions) {
      if (ex.queueStatus === 'RESOLVED') continue;
      const entry = map[ex.covenantId] || { open: 0, flip: 0 };
      entry.open += 1;
      if (ex.severity === 'CRITICAL') entry.flip += 1;
      map[ex.covenantId] = entry;
    }
    return map;
  }, [evidenceExceptions]);
  const openExceptionCount = useMemo(
    () => evidenceExceptions.filter(ex => ex.queueStatus !== 'RESOLVED').length,
    [evidenceExceptions]);
  const flipExceptionCount = useMemo(
    () => evidenceExceptions.filter(ex => ex.queueStatus !== 'RESOLVED' && ex.severity === 'CRITICAL').length,
    [evidenceExceptions]);

  // Resolve an evidence exception. mode ACCEPT_CERTIFIED keeps the certified
  // value as the record; ACCEPT_AUDITOR adjudicates the auditor's recomputation
  // as correct. Neither rewrites the certified test result — the adjudication
  // is recorded on the evidence row / exception / auditor notes.
  const resolveEvidenceException = async (ex: EvidenceException, mode: 'ACCEPT_CERTIFIED' | 'ACCEPT_AUDITOR') => {
    setResolvingId(ex.exceptionId);
    try {
      await axios.post(`/api/credit/covenants/evidence-exceptions/${ex.exceptionId}/resolve`, { mode });
      await fetchData();
    } catch (error) {
      console.error('Failed to resolve evidence exception:', error);
    } finally {
      setResolvingId(null);
    }
  };

  // Filter covenants
  const filteredOnly = covenants.filter(c => {
    if (statusFilter !== 'all' && c.currentStatus !== statusFilter) return false;
    if (typeFilter !== 'all' && c.category !== typeFilter) return false;
    if (validationFilter !== 'all') {
      const ex = exceptionsByCovenant[c.id];
      if (validationFilter === 'review' && !(ex && ex.open > 0)) return false;
      if (validationFilter === 'flip' && !(ex && ex.flip > 0)) return false;
      if (validationFilter === 'validated' && ((ex && ex.open > 0) || !c.auditorValidated)) return false;
    }
    if (search) {
      const s = search.toLowerCase();
      return c.covenantName.toLowerCase().includes(s) || c.borrowerName.toLowerCase().includes(s);
    }
    return true;
  });

  const filtered = filteredOnly;

  const breachCount = covenants.filter(c => c.currentStatus === 'BREACHED').length;
  const warningCount = covenants.filter(c => c.currentStatus === 'WARNING').length;
  const passedCount = covenants.filter(c => c.currentStatus === 'PASSED').length;
  const avgCushion = covenants.length > 0
    ? covenants.reduce((sum, c) => sum + c.cushion, 0) / covenants.length
    : 0;

  const categories = Array.from(new Set(covenants.map(c => c.category).filter(Boolean)));

  const covenantColumns = useMemo<ColDef<Covenant>[]>(() => [
    {
      field: 'covenantName',
      headerName: 'Covenant',
      minWidth: 230,
      pinned: 'left',
      cellRenderer: (params: any) => (
        <div style={{ fontWeight: 750, color: '#f8fafc' }}>{params.value}</div>
      ),
    },
    { field: 'borrowerName', headerName: 'Borrower', minWidth: 220 },
    {
      field: 'scope',
      headerName: 'Source',
      minWidth: 140,
      cellRenderer: (params: any) => {
        const cfg = SCOPE_LABELS[params.value || 'UNKNOWN'] || SCOPE_LABELS.UNKNOWN;
        return <ScopeBadge $color={cfg.color}>{cfg.label}</ScopeBadge>;
      },
      valueFormatter: params => (SCOPE_LABELS[params.value || 'UNKNOWN'] || SCOPE_LABELS.UNKNOWN).label,
    },
    {
      field: 'category',
      headerName: 'Category',
      minWidth: 130,
      valueGetter: params => params.data?.category || params.data?.covenantType || '',
    },
    {
      field: 'currentStatus',
      headerName: 'Status',
      minWidth: 130,
      cellRenderer: (params: any) => <StatusBadge $status={params.value}>{String(params.value || '').replace(/_/g, ' ')}</StatusBadge>,
    },
    {
      headerName: 'Validation',
      colId: 'evidenceValidation',
      minWidth: 130,
      valueGetter: params => {
        const ex = params.data ? exceptionsByCovenant[params.data.id] : undefined;
        if (ex && ex.flip > 0) return 'WOULD_FLIP';
        if (ex && ex.open > 0) return 'REVIEW';
        return params.data?.auditorValidated ? 'VALIDATED' : '';
      },
      cellRenderer: (params: any) => {
        const ex = params.data ? exceptionsByCovenant[params.data.id] : undefined;
        if (ex && ex.open > 0) {
          const isFlip = ex.flip > 0;
          return (
            <span
              title={isFlip
                ? `${ex.flip} exception(s) where the auditor value would flip PASS/FAIL`
                : `${ex.open} evidence exception(s) awaiting review`}
              style={{
                display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                fontSize: '0.7rem', fontWeight: 700,
                color: isFlip ? '#ef4444' : '#f59e0b',
                background: isFlip ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)',
              }}
            >
              {isFlip ? `⛔ ${ex.open} would flip` : `⚠ ${ex.open} review`}
            </span>
          );
        }
        if (params.data?.auditorValidated) {
          return (
            <span style={{
              display: 'inline-block', padding: '2px 8px', borderRadius: 4,
              fontSize: '0.7rem', fontWeight: 700,
              color: '#10b981', background: 'rgba(16,185,129,0.12)',
            }}>
              &#10003; validated
            </span>
          );
        }
        return <span style={{ color: '#475569', fontSize: '0.7rem' }}>—</span>;
      },
    },
    {
      field: 'actualValue',
      headerName: 'Actual',
      type: 'rightAligned',
      valueFormatter: params => params.data?.currentStatus === 'NOT_TESTED'
        ? '—'
        : formatMetricValue(Number(params.value), params.data?.metricType),
    },
    {
      field: 'thresholdValue',
      headerName: 'Threshold',
      type: 'rightAligned',
      valueFormatter: params => formatMetricValue(Number(params.value), params.data?.metricType),
    },
    {
      field: 'cushion',
      headerName: 'Cushion',
      type: 'rightAligned',
      sort: 'asc',
      cellRenderer: (params: any) => params.data?.currentStatus === 'NOT_TESTED'
        ? <span style={{ color: '#94a3b8' }}>—</span>
        : (
          <CushionIndicator $level={getCushionLevel(Number(params.value || 0))}>
            {Number(params.value || 0).toFixed(1)}%
          </CushionIndicator>
        ),
    },
    {
      headerName: 'Trend',
      minWidth: 120,
      valueGetter: params => params.data?.trendDirection || '',
      cellRenderer: (params: any) => (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 22 }}>
          {(params.data?.trendHistory || []).map((val: number, idx: number) => (
            <div key={idx} style={{
              width: 5,
              height: `${Math.max(2, Math.min(22, (val + 20) / 40 * 22))}px`,
              background: getTrendColor(val),
              borderRadius: 1,
            }} />
          ))}
        </div>
      ),
    },
    {
      field: 'quartersToBreachEst',
      headerName: 'Forecast',
      minWidth: 150,
      valueFormatter: params => {
        const q = params.value;
        return q == null || q < 0 ? '-' : `${q}Q to breach`;
      },
      comparator: (a, b) => {
        const norm = (n: number | null | undefined) => n == null || n < 0 ? Number.POSITIVE_INFINITY : n;
        return norm(a) - norm(b);
      },
      cellRenderer: (params: any) => {
        const q = params.value;
        if (q == null || q < 0) {
          return <span style={{ color: '#64748b', fontSize: '0.7rem' }}>—</span>;
        }
        const color = q <= 1 ? '#ef4444' : q <= 2 ? '#f59e0b' : '#94a3b8';
        const bg = q <= 1 ? 'rgba(239,68,68,0.12)' : q <= 2 ? 'rgba(245,158,11,0.12)' : 'rgba(148,163,184,0.12)';
        return (
          <span style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 4,
            fontSize: '0.7rem',
            fontWeight: 700,
            color,
            background: bg,
          }}>
            {q}Q to breach
          </span>
        );
      },
    },
    {
      field: 'nextTestDate',
      headerName: 'Next Test',
      minWidth: 130,
      valueFormatter: params => formatDateOnly(params.value),
    },
    {
      headerName: '',
      colId: 'loan360Link',
      minWidth: 150,
      pinned: 'right',
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        const cov = params.data as Covenant | undefined;
        if (!cov || !canOpenInLoan360(cov)) {
          return <span style={{ color: '#475569', fontSize: '0.7rem' }}>—</span>;
        }
        return (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openInLoan360(cov); }}
            style={{
              background: 'rgba(34,211,238,0.1)',
              border: '1px solid rgba(34,211,238,0.28)',
              color: '#22d3ee',
              borderRadius: 6,
              padding: '3px 9px',
              fontSize: '0.7rem',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Loan 360 &#8594;
          </button>
        );
      },
    },
  ], [canOpenInLoan360, openInLoan360, exceptionsByCovenant]);

  // #20/D6 — Exceptions queue columns. Resolution never rewrites the certified
  // value; both actions record the adjudication and clear the badge.
  const exceptionColumns = useMemo<ColDef<EvidenceException>[]>(() => [
    {
      field: 'severity',
      headerName: 'Severity',
      minWidth: 120,
      cellRenderer: (params: any) => {
        const isCritical = params.value === 'CRITICAL';
        return (
          <span style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: 4,
            fontSize: '0.7rem', fontWeight: 700,
            color: isCritical ? '#ef4444' : '#f59e0b',
            background: isCritical ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)',
          }}>
            {isCritical ? 'WOULD FLIP' : params.value}
          </span>
        );
      },
    },
    { field: 'borrowerName', headerName: 'Borrower', minWidth: 190 },
    { field: 'covenantName', headerName: 'Covenant', minWidth: 200 },
    {
      field: 'periodEndDate',
      headerName: 'Period',
      minWidth: 110,
      valueFormatter: params => formatDateOnly(params.value),
    },
    {
      field: 'certifiedValue',
      headerName: 'Certified',
      type: 'rightAligned',
      minWidth: 110,
      valueFormatter: params => params.value != null ? Number(params.value).toFixed(2) : '-',
    },
    {
      field: 'auditorValue',
      headerName: 'Auditor',
      type: 'rightAligned',
      minWidth: 110,
      cellRenderer: (params: any) => {
        const v = params.value != null ? Number(params.value).toFixed(2) : 'n/a';
        const critical = params.data?.severity === 'CRITICAL';
        return <span style={{ color: critical ? '#ef4444' : '#f8fafc', fontWeight: 700 }}>{v}</span>;
      },
    },
    {
      headerName: 'Threshold',
      minWidth: 120,
      valueGetter: params => params.data?.thresholdValue != null
        ? `${Number(params.data.thresholdValue).toFixed(2)} ${params.data.thresholdType || ''}`.trim()
        : '-',
    },
    {
      field: 'certifiedStatus',
      headerName: 'Verdict',
      minWidth: 110,
      cellRenderer: (params: any) => <StatusBadge $status={String(params.value || '')}>{params.value || '-'}</StatusBadge>,
    },
    { field: 'sourceDocumentName', headerName: 'Source Document', minWidth: 220 },
    {
      field: 'queueStatus',
      headerName: 'Queue',
      minWidth: 110,
      cellRenderer: (params: any) => (
        <span style={{
          color: params.value === 'RESOLVED' ? '#10b981' : '#94a3b8',
          fontSize: '0.72rem', fontWeight: 700,
        }}>
          {params.value}
        </span>
      ),
    },
    {
      headerName: 'Resolution',
      colId: 'resolutionActions',
      minWidth: 300,
      pinned: 'right',
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        const ex = params.data as EvidenceException | undefined;
        if (!ex) return null;
        if (ex.queueStatus === 'RESOLVED') {
          return (
            <span title={ex.resolution || ''} style={{ color: '#64748b', fontSize: '0.7rem' }}>
              {ex.resolvedBy ? `by ${ex.resolvedBy}` : 'resolved'}
            </span>
          );
        }
        const busy = resolvingId === ex.exceptionId;
        const btn = (label: string, mode: 'ACCEPT_CERTIFIED' | 'ACCEPT_AUDITOR', color: string) => (
          <button
            type="button"
            disabled={busy}
            onClick={(e) => { e.stopPropagation(); resolveEvidenceException(ex, mode); }}
            style={{
              background: `${color}1a`, border: `1px solid ${color}47`, color,
              borderRadius: 6, padding: '3px 9px', fontSize: '0.7rem', fontWeight: 700,
              cursor: busy ? 'wait' : 'pointer', marginRight: 6, opacity: busy ? 0.5 : 1,
            }}
          >
            {label}
          </button>
        );
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {btn('Accept certified', 'ACCEPT_CERTIFIED', '#10b981')}
            {btn('Accept auditor', 'ACCEPT_AUDITOR', '#f59e0b')}
          </div>
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [resolvingId]);

  return (
    <Container $embedded={embedded}>
      {!embedded && (
        <Header>
          <Title>Covenant Monitoring</Title>
          <HeaderActions>
            <ActionButton onClick={handleEvaluateAll} disabled={evaluating}>
              {evaluating ? 'Evaluating...' : 'Run Evaluation'}
            </ActionButton>
            <ActionButton $variant="primary">+ Add Covenant</ActionButton>
          </HeaderActions>
        </Header>
      )}

      <StatsRow>
        <StatCard $color="#10b981">
          <StatLabel>Passing</StatLabel>
          <StatValue>{passedCount}</StatValue>
        </StatCard>
        <StatCard $color="#f59e0b">
          <StatLabel>Warnings</StatLabel>
          <StatValue>{warningCount}</StatValue>
        </StatCard>
        <StatCard $color="#ef4444">
          <StatLabel>Breaches</StatLabel>
          <StatValue>{breachCount}</StatValue>
        </StatCard>
        <StatCard $color="#3b82f6">
          <StatLabel>Total</StatLabel>
          <StatValue>{covenants.length}</StatValue>
        </StatCard>
        <StatCard $color="#8b5cf6">
          <StatLabel>Avg Cushion</StatLabel>
          <StatValue>{avgCushion.toFixed(1)}%</StatValue>
        </StatCard>
      </StatsRow>

      {/* Alerts */}
      {alerts.length > 0 && (
        <AlertsSection>
          <SectionTitle>Active Alerts ({alerts.length})</SectionTitle>
          <AlertsGrid>
            {alerts.map(alert => (
              <AlertCard key={alert.id} $severity={alert.testResult} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <AlertHeader>
                  <AlertTitle>{alert.covenantName} - {alert.borrowerName}</AlertTitle>
                  <AlertBadge $severity={alert.testResult}>{alert.testResult}</AlertBadge>
                </AlertHeader>
                <AlertDetails>
                  <AlertDetail>
                    <DetailLabel>Actual Value</DetailLabel>
                    <DetailValue $highlight={alert.testResult === 'BREACHED'}>{formatMetricValue(alert.actualValue, alert.metricType)}</DetailValue>
                  </AlertDetail>
                  <AlertDetail>
                    <DetailLabel>Threshold</DetailLabel>
                    <DetailValue>{formatMetricValue(alert.thresholdValue, alert.metricType)}</DetailValue>
                  </AlertDetail>
                  <AlertDetail>
                    <DetailLabel>Cushion</DetailLabel>
                    <DetailValue $highlight={alert.cushion < 0}>{(alert.cushion ?? 0).toFixed(1)}%</DetailValue>
                  </AlertDetail>
                  <AlertDetail>
                    <DetailLabel>Test Date</DetailLabel>
                    <DetailValue>{formatDateOnly(alert.testDate)}</DetailValue>
                  </AlertDetail>
                </AlertDetails>
                <AlertActions>
                  <SmallButton onClick={() => {
                    const match = covenants.find(c => c.id === alert.id);
                    if (match) setSelectedCovenant(match);
                  }}>View Details</SmallButton>
                  {/* Workflows not yet built; disabled rather than ship dead buttons (BUG-005). */}
                  <SmallButton disabled title="Coming soon">Request Waiver</SmallButton>
                  <SmallButton disabled title="Coming soon">Add Note</SmallButton>
                </AlertActions>
              </AlertCard>
            ))}
          </AlertsGrid>
        </AlertsSection>
      )}

      {/* #20/D6 — view toggle: covenant register vs evidence exceptions queue */}
      <FilterBar>
        <FilterChip $active={view === 'covenants'} onClick={() => setView('covenants')}>
          Covenants ({covenants.length})
        </FilterChip>
        <FilterChip $active={view === 'exceptions'} onClick={() => setView('exceptions')}>
          Exceptions ({openExceptionCount})
          {flipExceptionCount > 0 && (
            <span style={{ color: '#ef4444', fontWeight: 800 }}> · {flipExceptionCount} would flip</span>
          )}
        </FilterChip>
      </FilterBar>

      {view === 'exceptions' ? (
        <>
          <SectionTitle>Covenant Evidence Exceptions</SectionTitle>
          <InstitutionalDataGrid
            title="Evidence exceptions queue"
            subtitle="Certified vs auditor-recomputed covenant values needing adjudication. Resolution records the decision — the certified value is never rewritten."
            rowData={evidenceExceptions}
            columnDefs={exceptionColumns}
            gridId="credit-covenant-evidence-exceptions"
            height={520}
            searchPlaceholder="Search borrower, covenant, document..."
            emptyLabel="No evidence exceptions — every covenant value is auditor-validated or adjudicated"
            getRowId={row => String(row.exceptionId)}
          />
        </>
      ) : (
        <>
      {/* Scope filter — unified Covenant Center: Leveraged Loans + Private Credit + ABF + RE Debt + Portco Debt */}
      <SectionTitle>All Covenants</SectionTitle>
      <FilterBar>
        {SCOPE_ORDER.map(s => {
          const cfg = s.key === 'all'
            ? { color: '#94a3b8' }
            : SCOPE_LABELS[s.key];
          return (
            <ScopeChip
              key={s.key}
              $active={scopeFilter === s.key}
              $color={cfg.color}
              onClick={() => handleScopeChange(s.key)}
            >
              {s.key !== 'all' && <ScopeChipDot $color={cfg.color} />}
              {s.short}
            </ScopeChip>
          );
        })}
      </FilterBar>
      <FilterBar>
        {[
          { key: 'all', label: `All (${covenants.length})` },
          { key: 'PASSED', label: `Passed (${passedCount})` },
          { key: 'WARNING', label: `Warning (${warningCount})` },
          { key: 'BREACHED', label: `Breached (${breachCount})` },
        ].map(f => (
          <FilterChip key={f.key} $active={statusFilter === f.key} onClick={() => setStatusFilter(f.key)}>
            {f.label}
          </FilterChip>
        ))}
        <span style={{ color: '#334155' }}>|</span>
        <FilterChip $active={typeFilter === 'all'} onClick={() => setTypeFilter('all')}>All Types</FilterChip>
        {categories.map(cat => (
          <FilterChip key={cat} $active={typeFilter === cat} onClick={() => setTypeFilter(cat!)}>
            {cat}
          </FilterChip>
        ))}
        <span style={{ color: '#334155' }}>|</span>
        {/* #20/D6 validation filter — auditor-evidence state, not covenant PASS/FAIL */}
        {[
          { key: 'all', label: 'Any Validation' },
          { key: 'validated', label: '✓ Validated' },
          { key: 'review', label: `⚠ Needs Review (${openExceptionCount})` },
          { key: 'flip', label: `⛔ Would Flip (${flipExceptionCount})` },
        ].map(f => (
          <FilterChip key={f.key} $active={validationFilter === f.key} onClick={() => setValidationFilter(f.key)}>
            {f.label}
          </FilterChip>
        ))}
        <SearchInput placeholder="Search borrower or covenant..." value={search} onChange={e => setSearch(e.target.value)} />
      </FilterBar>

      {/* Table + Detail Panel */}
      <ContentGrid>
        <InstitutionalDataGrid
          title="Unified covenant register"
          subtitle="AG Grid view across leveraged loans, private credit, ABF, RE debt and portfolio-company debt."
          rowData={filtered}
          columnDefs={covenantColumns}
          gridId="credit-unified-covenant-register"
          height={620}
          searchPlaceholder="Search covenant, borrower, source..."
          emptyLabel="No covenants match the current filters"
          getRowId={row => String(row.id)}
          onRowClicked={row => setSelectedCovenant(row)}
        />

        {/* Detail Panel */}
        <AnimatePresence mode="wait">
          {selectedCovenant ? (
            <DetailPanel key={selectedCovenant.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
              <DetailPanelTitle>{selectedCovenant.covenantName}</DetailPanelTitle>
              <DetailPanelSubtitle>{selectedCovenant.borrowerName}</DetailPanelSubtitle>

              <HealthScore $score={getHealthScore(selectedCovenant)}>
                <HealthScoreValue $score={getHealthScore(selectedCovenant)}>
                  {getHealthScore(selectedCovenant).toFixed(0)}
                </HealthScoreValue>
                <HealthScoreLabel>
                  <strong>Health Score</strong><br/>
                  {getHealthScore(selectedCovenant) >= 80 ? 'Healthy' : getHealthScore(selectedCovenant) >= 50 ? 'Caution' : 'Critical'}
                </HealthScoreLabel>
              </HealthScore>

              <DetailGrid>
                <DetailItem>
                  <DetailItemLabel>Status</DetailItemLabel>
                  <DetailItemValue><StatusBadge $status={selectedCovenant.currentStatus}>{selectedCovenant.currentStatus}</StatusBadge></DetailItemValue>
                </DetailItem>
                <DetailItem>
                  <DetailItemLabel>Source</DetailItemLabel>
                  <DetailItemValue>
                    {(() => {
                      const cfg = SCOPE_LABELS[selectedCovenant.scope || 'UNKNOWN'] || SCOPE_LABELS.UNKNOWN;
                      return <ScopeBadge $color={cfg.color}>{cfg.label}</ScopeBadge>;
                    })()}
                  </DetailItemValue>
                </DetailItem>
                <DetailItem>
                  <DetailItemLabel>Category</DetailItemLabel>
                  <DetailItemValue>{selectedCovenant.category || selectedCovenant.covenantType}</DetailItemValue>
                </DetailItem>
                <DetailItem>
                  <DetailItemLabel>Actual</DetailItemLabel>
                  <DetailItemValue $color={selectedCovenant.cushion < 0 ? '#ef4444' : undefined}>
                    {formatMetricValue(selectedCovenant.actualValue, selectedCovenant.metricType)}
                  </DetailItemValue>
                </DetailItem>
                <DetailItem>
                  <DetailItemLabel>Threshold</DetailItemLabel>
                  <DetailItemValue>
                    {formatMetricValue(selectedCovenant.thresholdValue, selectedCovenant.metricType)}
                  </DetailItemValue>
                </DetailItem>
                <DetailItem>
                  <DetailItemLabel>Cushion</DetailItemLabel>
                  <DetailItemValue $color={selectedCovenant.cushion < 0 ? '#ef4444' : selectedCovenant.cushion < 15 ? '#f59e0b' : '#10b981'}>
                    {(selectedCovenant.cushion ?? 0).toFixed(1)}%
                  </DetailItemValue>
                </DetailItem>
                <DetailItem>
                  <DetailItemLabel>Frequency</DetailItemLabel>
                  <DetailItemValue>{selectedCovenant.frequency}</DetailItemValue>
                </DetailItem>
                <DetailItem>
                  <DetailItemLabel>Next Test</DetailItemLabel>
                  <DetailItemValue>{formatDateOnly(selectedCovenant.nextTestDate)}</DetailItemValue>
                </DetailItem>
                <DetailItem>
                  <DetailItemLabel>Warning At</DetailItemLabel>
                  <DetailItemValue>{selectedCovenant.warningThreshold}%</DetailItemValue>
                </DetailItem>
              </DetailGrid>

              {/* Auditor validation — deterministic second-pair-of-eyes check.
                  Surfaces the differentiator: the value was extracted (operator / LLM)
                  AND independently re-derived by the engine. */}
              {selectedCovenant.auditorValidated ? (
                <AuditorChip $validated>
                  <AuditorChipIcon $validated>&#10003;</AuditorChipIcon>
                  <AuditorChipBody>
                    <AuditorChipTitle $validated>Auditor-validated (engine)</AuditorChipTitle>
                    <AuditorChipDetail>
                      {selectedCovenant.auditorActualValue != null && (
                        <>Recomputed {formatMetricValue(selectedCovenant.auditorActualValue, selectedCovenant.metricType)}</>
                      )}
                      {selectedCovenant.auditorNotes
                        ? `${selectedCovenant.auditorActualValue != null ? ' · ' : ''}${selectedCovenant.auditorNotes}`
                        : ''}
                      {selectedCovenant.auditorRunAt && (
                        <> · {new Date(selectedCovenant.auditorRunAt).toLocaleDateString()}</>
                      )}
                    </AuditorChipDetail>
                  </AuditorChipBody>
                </AuditorChip>
              ) : (
                <AuditorChip $validated={false}>
                  <AuditorChipIcon $validated={false}>&#9675;</AuditorChipIcon>
                  <AuditorChipBody>
                    <AuditorChipTitle $validated={false}>Not independently validated</AuditorChipTitle>
                    <AuditorChipDetail>
                      {selectedCovenant.auditorNotes
                        ? selectedCovenant.auditorNotes
                        : 'No deterministic auditor re-derivation on the latest test result yet.'}
                    </AuditorChipDetail>
                  </AuditorChipBody>
                </AuditorChip>
              )}

              {/* Trend Chart */}
              <TrendSection>
                <TrendTitle>
                  Cushion Trend
                  {selectedCovenant.trendDirection && (
                    <span style={{
                      marginLeft: 12,
                      fontSize: '0.7rem',
                      padding: '2px 8px',
                      borderRadius: 4,
                      color: selectedCovenant.trendDirection === 'DETERIORATING' ? '#ef4444'
                           : selectedCovenant.trendDirection === 'IMPROVING' ? '#10b981'
                           : '#94a3b8',
                      background: selectedCovenant.trendDirection === 'DETERIORATING' ? 'rgba(239,68,68,0.12)'
                               : selectedCovenant.trendDirection === 'IMPROVING' ? 'rgba(16,185,129,0.12)'
                               : 'rgba(148,163,184,0.12)',
                    }}>
                      {selectedCovenant.trendDirection}
                    </span>
                  )}
                  {selectedCovenant.quartersToBreachEst != null && selectedCovenant.quartersToBreachEst >= 0 && (
                    <span style={{
                      marginLeft: 8,
                      fontSize: '0.7rem',
                      padding: '2px 8px',
                      borderRadius: 4,
                      color: selectedCovenant.quartersToBreachEst <= 2 ? '#f59e0b' : '#94a3b8',
                      background: selectedCovenant.quartersToBreachEst <= 2 ? 'rgba(245,158,11,0.12)' : 'rgba(148,163,184,0.12)',
                    }}>
                      ~{selectedCovenant.quartersToBreachEst}Q to breach
                    </span>
                  )}
                </TrendTitle>
                {selectedCovenant.trendPoints && selectedCovenant.trendPoints.length > 0 ? (
                  <InstitutionalChart
                    height={180}
                    options={{
                      chart: { type: 'line' },
                      title: { text: '' },
                      xAxis: {
                        categories: selectedCovenant.trendPoints.map(p => {
                          const d = parseLocalDate(p.testDate) || new Date(p.testDate);
                          const q = Math.floor(d.getMonth() / 3) + 1;
                          const yy = String(d.getFullYear()).slice(-2);
                          return `Q${q} '${yy}`;
                        }),
                      },
                      yAxis: {
                        title: { text: 'Cushion %' },
                        plotLines: [{
                          value: 0,
                          color: '#ef4444',
                          width: 1,
                          dashStyle: 'Dash',
                        }],
                      },
                      tooltip: {
                        valueSuffix: '%',
                        valueDecimals: 1,
                      },
                      plotOptions: {
                        line: {
                          marker: { enabled: true, radius: 3 },
                          lineWidth: 2,
                        },
                      },
                      series: [{
                        type: 'line',
                        name: 'Cushion',
                        data: selectedCovenant.trendPoints.map(p => Number(p.cushion.toFixed(2))),
                      }],
                    }}
                  />
                ) : (
                  <div style={{ color: '#64748b', fontSize: '0.78rem', padding: '1rem 0' }}>
                    No test history
                  </div>
                )}
              </TrendSection>

              {/* Actions — workflows not yet built; disabled so the screen does not ship
                  dead buttons that imply working actions (see BUG-005). */}
              <DetailActions>
                {/* Cross-link to the loan's Loan 360 drawer in the Credit Operating
                    System. Real, working action (unlike the disabled BUG-005 stubs
                    below). Shown only for scopes with a Loan 360 surface. */}
                {canOpenInLoan360(selectedCovenant) && (
                  <DetailActionButton $color="#22d3ee" onClick={() => openInLoan360(selectedCovenant)}>
                    Open in Loan 360 &#8594;
                  </DetailActionButton>
                )}
                {selectedCovenant.currentStatus === 'BREACHED' && (
                  <DetailActionButton $color="#ef4444" disabled title="Coming soon">Request Waiver</DetailActionButton>
                )}
                {selectedCovenant.currentStatus === 'BREACHED' && (
                  <DetailActionButton $color="#3b82f6" disabled title="Coming soon">Apply Equity Cure</DetailActionButton>
                )}
                <DetailActionButton $color="#8b5cf6" disabled title="Coming soon">View Test History</DetailActionButton>
                <DetailActionButton disabled title="Coming soon">Export Report</DetailActionButton>
              </DetailActions>
            </DetailPanel>
          ) : (
            <DetailPanel key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div style={{ textAlign: 'center', padding: '2rem 0', color: '#64748b' }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>&#128203;</div>
                <div style={{ fontSize: '0.85rem' }}>Select a covenant to view details, trend history, and take actions</div>
              </div>
            </DetailPanel>
          )}
        </AnimatePresence>
      </ContentGrid>
        </>
      )}
    </Container>
  );
};

export default CovenantMonitoringScreen;
